import {
  PdfTemplateCatalogueCategory,
  PdfTemplateField,
  PdfTemplateFieldMapping,
  PdfTemplateFormField,
  PdfTemplateFormFieldType,
  PdfTemplateValueStatus,
} from "@/data/models/ClientAdministrationRow";
import { isProblemStatus } from "@/utils/ClientPdfTemplateList";

/**
 * The PDF template mapping editor's logic (admin portal, PDF-Vorlagen): which rows the editor shows,
 * how a field's mapping is edited, and how it prints. Pure; IRES Cloud repeats every rule that matters
 * on save (`saveClientPdfTemplateMappingForAdministration`).
 *
 * A mapping links one form field to one category and an ordered list of values, with optional text
 * before or after a value. It prints the way IRES Admin and IRES Project print it: the values in order,
 * separated by a space, a comma (`COMMA`) attached to what precedes it.
 */

/** The comma IRES Admin offers for every text field, whatever the category. */
export const COMMA_VALUE = "COMMA";

/** IRES Cloud's limit for a text before or after a value. */
export const MAX_TRANSFORMATION_TEXT_LENGTH = 200;

export type PdfTemplateFieldFilter = "ALL" | "LINKED" | "UNLINKED";

export const PDF_TEMPLATE_FIELD_FILTERS: PdfTemplateFieldFilter[] = ["ALL", "LINKED", "UNLINKED"];

export type TransformationType = "PREFIX" | "POSTFIX";

/** One row of the editor: a form field of the PDF, or a mapped field the PDF no longer has. */
export interface PdfTemplateEditorRow {
  fieldName: string;
  /** Null when the PDF could not be read or no longer has the field. */
  type: PdfTemplateFormFieldType | null;
  childFieldCount: number;
  /** False for a mapped field the PDF no longer has; null when the PDF could not be read. */
  inPdf: boolean | null;
  /** Null for a field nothing is linked to. */
  mapping: PdfTemplateFieldMapping | null;
}

/** A value the editor can name: its German label and what IRES Cloud does with it. */
export interface PdfTemplateValueOption {
  name: string;
  label: string;
  status: PdfTemplateValueStatus | null;
}

/**
 * The stored mappings, from the template row IRES Cloud returns, in the editor's shape.
 *
 * @param fields - the row's fields.
 * @returns one mapping per field; a field stored without a category keeps an empty one.
 */
export function mappingsFromTemplateFields(fields: PdfTemplateField[]): PdfTemplateFieldMapping[] {
  return fields.map((field) => ({
    fieldName: field.fieldName,
    category: field.category ?? "",
    values: field.values.map((value) => value.name),
    transformations: field.transformations
      .filter((transformation) => transformation.type === "PREFIX" || transformation.type === "POSTFIX")
      .map((transformation) => ({
        id: transformation.id,
        fieldValue: transformation.value,
        type: transformation.type as TransformationType,
        value: transformation.text,
      })),
  }));
}

/**
 * The editor's rows: every form field of the PDF in the PDF's order, then the mapped fields the PDF
 * no longer has. Without the PDF's fields, only the mapped fields.
 *
 * @param formFields - the PDF's top-level fields, or null when they could not be read.
 * @param mappings - the stored mappings.
 * @returns the rows.
 */
export function buildEditorRows(
  formFields: PdfTemplateFormField[] | null,
  mappings: PdfTemplateFieldMapping[]
): PdfTemplateEditorRow[] {
  const byName = new Map(mappings.map((mapping) => [mapping.fieldName, mapping]));

  if (formFields === null) {
    return mappings.map((mapping) => ({
      fieldName: mapping.fieldName,
      type: null,
      childFieldCount: 0,
      inPdf: null,
      mapping,
    }));
  }

  const inPdf = new Set(formFields.map((field) => field.name));

  return [
    ...formFields.map((field) => ({
      fieldName: field.name,
      type: field.type,
      childFieldCount: field.childFieldCount,
      inPdf: true,
      mapping: byName.get(field.name) ?? null,
    })),
    ...mappings
      .filter((mapping) => !inPdf.has(mapping.fieldName))
      .map((mapping) => ({ fieldName: mapping.fieldName, type: null, childFieldCount: 0, inPdf: false, mapping })),
  ];
}

/** Whether something is linked to the row's field. */
export function isLinked(row: PdfTemplateEditorRow): boolean {
  return row.mapping !== null && row.mapping.values.length > 0;
}

/** The rows a filter shows. */
export function filterEditorRows(rows: PdfTemplateEditorRow[], filter: PdfTemplateFieldFilter): PdfTemplateEditorRow[] {
  return filter === "ALL" ? rows : rows.filter((row) => isLinked(row) === (filter === "LINKED"));
}

/** What a save sends: every linked field, in the rows' order. */
export function mappingsToSave(rows: PdfTemplateEditorRow[]): PdfTemplateFieldMapping[] {
  return rows.filter(isLinked).map((row) => row.mapping as PdfTemplateFieldMapping);
}

/**
 * A comparable form of what a save would send, for "has anything changed". Transformation ids are left
 * out (a new text has none until saved) and transformations are put in a fixed order.
 */
export function mappingsKey(mappings: PdfTemplateFieldMapping[]): string {
  return JSON.stringify(
    mappings.map((mapping) => [
      mapping.fieldName,
      mapping.category,
      mapping.values,
      mapping.transformations
        .map((transformation) => [transformation.fieldValue, transformation.type, transformation.value])
        .sort((a, b) => a.join("\u0000").localeCompare(b.join("\u0000"))),
    ])
  );
}

/** Whether a field takes text around its values and a comma: a text field, or one whose kind is unknown. */
export function takesText(type: PdfTemplateFormFieldType | null): boolean {
  return type === null || type === "TEXT";
}

/** The text placed before (`PREFIX`) or after (`POSTFIX`) a value, or "". */
export function transformationText(mapping: PdfTemplateFieldMapping, value: string, type: TransformationType): string {
  return (
    mapping.transformations.find((transformation) => transformation.fieldValue === value && transformation.type === type)
      ?.value ?? ""
  );
}

/**
 * The mapping with the text before or after a value set; an empty text removes it. An existing text
 * keeps its id, so IRES Admin still recognises it.
 */
export function withTransformationText(
  mapping: PdfTemplateFieldMapping,
  value: string,
  type: TransformationType,
  text: string
): PdfTemplateFieldMapping {
  const matches = (transformation: PdfTemplateFieldMapping["transformations"][number]) =>
    transformation.fieldValue === value && transformation.type === type;
  const existing = mapping.transformations.find(matches);
  const others = mapping.transformations.filter((transformation) => !matches(transformation));

  return {
    ...mapping,
    transformations:
      text === "" ? others : [...others, { id: existing?.id ?? null, fieldValue: value, type, value: text }],
  };
}

/** Drops the texts of values the mapping no longer has. */
function withoutOrphanTexts(mapping: PdfTemplateFieldMapping): PdfTemplateFieldMapping {
  return {
    ...mapping,
    transformations: mapping.transformations.filter((transformation) => mapping.values.includes(transformation.fieldValue)),
  };
}

/** The mapping with another category: its values and texts go, as in IRES Admin. */
export function withCategory(mapping: PdfTemplateFieldMapping, category: string): PdfTemplateFieldMapping {
  return category === mapping.category ? mapping : { ...mapping, category, values: [], transformations: [] };
}

/**
 * The mapping with a value added: appended, or for a checkbox replacing the value it had (a checkbox
 * holds one value, IRES Admin's rule).
 */
export function withValueAdded(
  mapping: PdfTemplateFieldMapping,
  value: string,
  type: PdfTemplateFormFieldType | null
): PdfTemplateFieldMapping {
  return withoutOrphanTexts({ ...mapping, values: type === "CHECK_BOX" ? [value] : [...mapping.values, value] });
}

/** The mapping without the value at `index`, and without its texts if it no longer appears. */
export function withValueRemoved(mapping: PdfTemplateFieldMapping, index: number): PdfTemplateFieldMapping {
  return withoutOrphanTexts({ ...mapping, values: mapping.values.filter((_, position) => position !== index) });
}

/** The mapping with the value at `index` moved by `delta` places; unchanged when that leaves the list. */
export function withValueMoved(mapping: PdfTemplateFieldMapping, index: number, delta: number): PdfTemplateFieldMapping {
  const target = index + delta;

  if (index < 0 || index >= mapping.values.length || target < 0 || target >= mapping.values.length) {
    return mapping;
  }

  const values = [...mapping.values];
  [values[index], values[target]] = [values[target], values[index]];
  return { ...mapping, values };
}

/**
 * The mapping with the value at `from` dropped in front of the value now at `before` (`values.length`
 * drops it at the end), as dragging places it; unchanged when that leaves it where it was.
 */
export function withValueMovedBefore(mapping: PdfTemplateFieldMapping, from: number, before: number): PdfTemplateFieldMapping {
  const count = mapping.values.length;

  if (from < 0 || from >= count || before < 0 || before > count || before === from || before === from + 1) {
    return mapping;
  }

  const values = [...mapping.values];
  const [moved] = values.splice(from, 1);
  values.splice(before > from ? before - 1 : before, 0, moved);
  return { ...mapping, values };
}

/** Field names a save links anew, changes, and unlinks, against the mapping as loaded. */
export interface PdfTemplateMappingEdits {
  added: string[];
  changed: string[];
  removed: string[];
}

/**
 * What a save would change, for the confirmation before it: compared field by field with the same
 * rules as {@link mappingsKey}, so a field counts as changed exactly when SICHERN would be enabled by it.
 */
export function describeMappingEdits(
  before: PdfTemplateFieldMapping[],
  after: PdfTemplateFieldMapping[]
): PdfTemplateMappingEdits {
  const linked = (mappings: PdfTemplateFieldMapping[]) =>
    new Map(mappings.filter((mapping) => mapping.values.length > 0).map((mapping) => [mapping.fieldName, mapping]));
  const old = linked(before);
  const next = linked(after);

  return {
    added: [...next.keys()].filter((name) => !old.has(name)),
    changed: [...next.entries()]
      .filter(([name, mapping]) => {
        const previous = old.get(name);
        return previous !== undefined && mappingsKey([previous]) !== mappingsKey([mapping]);
      })
      .map(([name]) => name),
    removed: [...old.keys()].filter((name) => !next.has(name)),
  };
}

/**
 * Every value name the editor may show, with its label and IRES Cloud status: the catalogue's, then
 * the stored ones it no longer offers (legacy values keep the label IRES Cloud gave them).
 */
export function valueOptionsByName(
  catalogue: PdfTemplateCatalogueCategory[],
  storedFields: PdfTemplateField[]
): Map<string, PdfTemplateValueOption> {
  const options = new Map<string, PdfTemplateValueOption>();

  for (const category of catalogue) {
    for (const value of category.values) {
      options.set(value.name, { name: value.name, label: value.label, status: value.status });
    }
  }

  for (const field of storedFields) {
    for (const value of field.values) {
      if (!options.has(value.name)) {
        options.set(value.name, { name: value.name, label: value.label || value.name, status: value.status });
      }
    }
  }

  return options;
}

/**
 * The values offered for a field in a category: the category's own, plus the comma for a text field
 * (IRES Admin adds it to every category). Values the mapping already holds stay addable, so a comma
 * can repeat.
 */
export function offeredValues(
  catalogue: PdfTemplateCatalogueCategory[],
  category: string,
  type: PdfTemplateFormFieldType | null,
  options: Map<string, PdfTemplateValueOption>
): PdfTemplateValueOption[] {
  const values: PdfTemplateValueOption[] = (catalogue.find((entry) => entry.name === category)?.values ?? []).map(
    (value) => ({ name: value.name, label: value.label, status: value.status })
  );

  if (takesText(type) && values.length > 0 && !values.some((value) => value.name === COMMA_VALUE)) {
    const comma = options.get(COMMA_VALUE);
    values.push({ name: COMMA_VALUE, label: comma?.label ?? ",", status: comma?.status ?? null });
  }

  return values;
}

/**
 * The mapping as it prints, with labels standing in for the project's data: values in order, a space
 * between them, a comma attached to what precedes it, each value's text before and after it.
 */
export function previewText(mapping: PdfTemplateFieldMapping, labelOf: (value: string) => string): string {
  let text = "";

  for (const value of mapping.values) {
    if (value === COMMA_VALUE) {
      text += ",";
      continue;
    }

    const part =
      transformationText(mapping, value, "PREFIX") + labelOf(value) + transformationText(mapping, value, "POSTFIX");
    text += text === "" ? part : ` ${part}`;
  }

  return text;
}

/** One translatable sentence: an i18n key and its positional parameters. */
export interface PdfTemplateMessagePart {
  key: string;
  params: string[];
}

/** Whose template is edited, for the banner and the confirmation. */
export interface PdfTemplateScope {
  isShared: boolean;
  isCertificateTemplate: boolean;
  /** The client's name (or id); unused for a shared template. */
  clientName: string;
}

/**
 * What a save reaches, as sentences. A client's template: every new PDF IRES Project makes for that
 * client (and its HyDry certificate, if this is that template). A shared one: only later take-overs,
 * since every client that took it over works on its own copy.
 */
export function scopeNoticeParts(scope: PdfTemplateScope): PdfTemplateMessagePart[] {
  if (scope.isShared) {
    return [{ key: "admin.pdfTemplates.edit.scopeShared", params: [] }];
  }

  return [
    { key: "admin.pdfTemplates.edit.scopeClient", params: [scope.clientName] },
    ...(scope.isCertificateTemplate ? [{ key: "admin.pdfTemplates.edit.scopeCertificate", params: [] }] : []),
  ];
}

/**
 * The confirmation before a save: whose template and what it reaches, then which fields are linked
 * anew, changed and unlinked. Two paragraphs; the second lists only what applies.
 */
export function saveConfirmationParts(
  scope: PdfTemplateScope,
  templateName: string,
  edits: PdfTemplateMappingEdits
): { scope: PdfTemplateMessagePart[]; changes: PdfTemplateMessagePart[] } {
  const list = (key: string, names: string[]) => (names.length > 0 ? [{ key, params: [names.join(", ")] }] : []);

  return {
    scope: [
      scope.isShared
        ? { key: "admin.pdfTemplates.edit.confirmShared", params: [templateName] }
        : { key: "admin.pdfTemplates.edit.confirmClient", params: [templateName, scope.clientName] },
      ...(scope.isCertificateTemplate && !scope.isShared
        ? [{ key: "admin.pdfTemplates.edit.scopeCertificate", params: [] }]
        : []),
    ],
    changes: [
      ...list("admin.pdfTemplates.edit.changesAdded", edits.added),
      ...list("admin.pdfTemplates.edit.changesChanged", edits.changed),
      ...list("admin.pdfTemplates.edit.changesRemoved", edits.removed),
    ],
  };
}

/**
 * The IRES Cloud status that makes a value a problem (blank or always unticked), where it matters: only
 * on the HyDry certificate's template, the one template IRES Cloud fills. Null everywhere else.
 */
export function cloudProblemStatus(
  value: string,
  options: Map<string, PdfTemplateValueOption>,
  showsCloudStatuses: boolean
): PdfTemplateValueStatus | null {
  const status = options.get(value)?.status ?? null;
  return showsCloudStatuses && status !== null && isProblemStatus(status) ? status : null;
}

/**
 * How a field is described under its name, as i18n keys: its kind, and the group size for a parent of
 * child fields ("Textfeld · Gruppe aus 2 Feldern"), or only that the PDF no longer has it.
 */
export function fieldDescriptionParts(row: PdfTemplateEditorRow): Array<{ key: string; count?: number }> {
  if (row.inPdf === false) {
    return [{ key: "admin.pdfTemplates.edit.notInPdf" }];
  }

  return [
    ...(row.type ? [{ key: `admin.pdfTemplates.fieldTypes.${row.type}` }] : []),
    ...(row.childFieldCount > 0 ? [{ key: "admin.pdfTemplates.edit.childFields", count: row.childFieldCount }] : []),
  ];
}

/**
 * The snackbar message for a failed save: changed meanwhile (IRES Admin or another portal user saved
 * the template), refused content (naming the field), or a plain failure.
 */
export function saveErrorMessage(error: unknown): { key: string; params?: string[] } {
  const failure = (error ?? {}) as { code?: unknown; details?: { fieldName?: unknown } | null };
  const code = typeof failure.code === "string" ? failure.code.replace(/^functions\//, "") : "";

  if (code === "aborted") {
    return { key: "admin.pdfTemplates.edit.changedMeanwhile" };
  }

  if (code === "invalid-argument" && typeof failure.details?.fieldName === "string") {
    return { key: "admin.pdfTemplates.edit.saveRefused", params: [failure.details.fieldName] };
  }

  return { key: "admin.pdfTemplates.edit.saveFailed" };
}
